import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import SiteHeader from "@/components/SiteHeader";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Fuel, Truck, Gauge, Wrench, TrendingUp, Award, Route } from "lucide-react";
import StrategyCallDialog from "@/components/StrategyCallDialog";
import { STRATEGIST } from "@/data/strategist";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const money = (n) => `$${(isFinite(n) ? n : 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
const perMile = (n) => `$${(isFinite(n) ? n : 0).toFixed(3)}`;

const EQUIPMENT = [
  { id: "dry_van", label: "Dry Van" },
  { id: "reefer", label: "Reefer" },
  { id: "flatbed", label: "Flatbed" },
  { id: "step_deck", label: "Step Deck" },
  { id: "box_truck", label: "Box Truck" },
  { id: "power_only", label: "Power Only" },
];
const eqLabel = (id) => EQUIPMENT.find((e) => e.id === id)?.label || "owner";

const Field = ({ id, label, value, onChange, prefix, suffix, step = "any" }) => (
  <div className="space-y-1.5">
    <Label htmlFor={id} className="text-sm text-slate-700">{label}</Label>
    <div className="relative">
      {prefix && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">{prefix}</span>}
      <Input
        id={id}
        data-testid={`cpm-${id}`}
        type="number"
        min="0"
        step={step}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`h-11 font-mono tabular ${prefix ? "pl-7" : ""} ${suffix ? "pr-14" : ""}`}
      />
      {suffix && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs">{suffix}</span>}
    </div>
  </div>
);

export default function CPMCalculator() {
  const [benchmark, setBenchmark] = useState(null);
  const [equipment, setEquipment] = useState("dry_van");
  const [f, setF] = useState({
    payment: 2200, insurance: 1200, permits: 300, otherFixed: 400,
    diesel: 4.0, mpg: 6.5, maintenance: 0.15, tires: 0.04, tollsMisc: 0.05,
    miles: 10000, margin: 20,
  });
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));
  const num = (v) => (parseFloat(v) || 0);

  const r = useMemo(() => {
    const miles = num(f.miles);
    const fixedMonthly = num(f.payment) + num(f.insurance) + num(f.permits) + num(f.otherFixed);
    const fixedCPM = miles > 0 ? fixedMonthly / miles : 0;
    const fuelCPM = num(f.mpg) > 0 ? num(f.diesel) / num(f.mpg) : 0;
    const variableCPM = fuelCPM + num(f.maintenance) + num(f.tires) + num(f.tollsMisc);
    const totalCPM = fixedCPM + variableCPM;
    const suggestedRate = totalCPM * (1 + num(f.margin) / 100);
    const monthlyOperating = totalCPM * miles;
    return { miles, fixedMonthly, fixedCPM, fuelCPM, variableCPM, totalCPM, suggestedRate, monthlyOperating };
  }, [f]);

  const fixedPct = r.totalCPM > 0 ? (r.fixedCPM / r.totalCPM) * 100 : 0;

  useEffect(() => {
    localStorage.setItem(
      "routehaul_cpm_v1",
      JSON.stringify({ inputs: f, equipment, results: r, updated_at: new Date().toISOString() })
    );
  }, [f, equipment, r]);

  const standing = useMemo(() => {
    if (!benchmark || benchmark.sample_size < 5) return null;
    const c = r.totalCPM;
    if (c <= benchmark.p25) return { label: "Top 25% — lean operator", cls: "bg-emerald-500 text-white" };
    if (c <= benchmark.median_cpm) return { label: "Better than average", cls: "bg-emerald-400 text-slate-900" };
    if (c <= benchmark.p75) return { label: "Above average cost", cls: "bg-amber-400 text-slate-900" };
    return { label: "High cost — trim first", cls: "bg-red-500 text-white" };
  }, [benchmark, r.totalCPM]);

  useEffect(() => {
    const t = setTimeout(() => {
      axios.post(`${API}/cpm/save`, {
        total_cpm: r.totalCPM,
        suggested_rate: r.suggestedRate,
        miles: r.miles,
        margin: num(f.margin),
        equipment,
      }).then(() => axios.get(`${API}/benchmarks?equipment=${equipment}`)).then(({ data }) => setBenchmark(data)).catch(() => {});
    }, 1200);
    return () => clearTimeout(t);
  }, [r, equipment]); // eslint-disable-line

  useEffect(() => {
    axios.get(`${API}/benchmarks?equipment=${equipment}`).then(({ data }) => setBenchmark(data)).catch(() => {});
  }, [equipment]);

  return (
    <div className="min-h-screen bg-white">
      <SiteHeader />
      <section className="max-w-7xl mx-auto px-6 pt-14 pb-8">
        <div className="text-xs tracking-[0.2em] uppercase font-bold text-brand mb-3">Owner-operator tools</div>
        <h1 className="font-heading text-4xl sm:text-5xl tracking-tighter font-black text-slate-900">Cost-Per-Mile Calculator</h1>
        <p className="mt-4 text-slate-600 max-w-2xl leading-relaxed">
          Know your true cost per mile before you accept a load. Enter your numbers below — everything updates instantly.
        </p>
      </section>

      <section className="max-w-7xl mx-auto px-6 pb-24 grid lg:grid-cols-5 gap-8 items-start">
        {/* Inputs */}
        <div className="lg:col-span-3 space-y-8">
          <div className="border border-slate-200 rounded-md p-6">
            <div className="flex items-center gap-2 mb-5">
              <Truck className="w-4 h-4 text-brand" />
              <h2 className="font-heading text-lg font-semibold text-slate-900">Fixed monthly costs</h2>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field id="payment" label="Truck / trailer payment" value={f.payment} onChange={set("payment")} prefix="$" />
              <Field id="insurance" label="Insurance" value={f.insurance} onChange={set("insurance")} prefix="$" />
              <Field id="permits" label="Permits & compliance (IFTA, UCR, ELD)" value={f.permits} onChange={set("permits")} prefix="$" />
              <Field id="otherFixed" label="Parking / other fixed" value={f.otherFixed} onChange={set("otherFixed")} prefix="$" />
            </div>
          </div>

          <div className="border border-slate-200 rounded-md p-6">
            <div className="flex items-center gap-2 mb-5">
              <Fuel className="w-4 h-4 text-brand" />
              <h2 className="font-heading text-lg font-semibold text-slate-900">Variable costs</h2>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field id="diesel" label="Diesel price" value={f.diesel} onChange={set("diesel")} prefix="$" suffix="/gal" />
              <Field id="mpg" label="Truck fuel economy" value={f.mpg} onChange={set("mpg")} suffix="MPG" />
              <Field id="maintenance" label="Maintenance" value={f.maintenance} onChange={set("maintenance")} prefix="$" suffix="/mi" />
              <Field id="tires" label="Tires" value={f.tires} onChange={set("tires")} prefix="$" suffix="/mi" />
              <Field id="tollsMisc" label="Tolls & misc" value={f.tollsMisc} onChange={set("tollsMisc")} prefix="$" suffix="/mi" />
            </div>
          </div>

          <div className="border border-slate-200 rounded-md p-6">
            <div className="flex items-center gap-2 mb-5">
              <Gauge className="w-4 h-4 text-brand" />
              <h2 className="font-heading text-lg font-semibold text-slate-900">Operation</h2>
            </div>
            <div className="mb-5">
              <Label className="text-sm text-slate-700">Your equipment</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {EQUIPMENT.map((e) => (
                  <button
                    key={e.id}
                    type="button"
                    onClick={() => setEquipment(e.id)}
                    data-testid={`cpm-equipment-${e.id}`}
                    className={`px-3 h-9 rounded-full text-xs font-bold transition-colors ${
                      equipment === e.id ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                    }`}
                  >
                    {e.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field id="miles" label="Miles driven per month" value={f.miles} onChange={set("miles")} suffix="mi" />
              <Field id="margin" label="Target profit margin" value={f.margin} onChange={set("margin")} suffix="%" />
            </div>
          </div>
        </div>

        {/* Results */}
        <div className="lg:col-span-2 lg:sticky lg:top-24">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-slate-900 text-white rounded-md p-8"
          >
            <div className="text-xs tracking-[0.2em] uppercase font-bold text-slate-400 mb-2">Your true cost per mile</div>
            <div className="font-mono font-bold tabular text-brand leading-none" data-testid="cpm-total" style={{ fontSize: "clamp(3.5rem, 8vw, 5rem)" }}>
              {perMile(r.totalCPM)}
            </div>
            <div className="text-slate-500 text-sm mt-1">break-even rate per mile</div>
            {standing && (
              <span data-testid="standing-badge" className={`inline-flex items-center gap-1.5 mt-4 px-3 py-1.5 rounded-full text-xs font-bold ${standing.cls}`}>
                <Award className="w-3.5 h-3.5" /> {standing.label}
              </span>
            )}

            <div className="mt-8 space-y-3">
              <Row label="Fixed cost / mile" value={perMile(r.fixedCPM)} />
              <Row label="Fuel cost / mile" value={perMile(r.fuelCPM)} />
              <Row label="Variable cost / mile" value={perMile(r.variableCPM)} />
              <div className="h-px bg-white/10 my-2" />
              <Row label={`Suggested rate (+${num(f.margin)}% margin)`} value={perMile(r.suggestedRate)} highlight testid="cpm-suggested" />
            </div>

            {/* breakdown bar */}
            <div className="mt-8">
              <div className="flex h-2.5 rounded-full overflow-hidden bg-white/10">
                <div className="bg-brand" style={{ width: `${fixedPct}%` }} />
                <div className="bg-emerald-400" style={{ width: `${100 - fixedPct}%` }} />
              </div>
              <div className="flex justify-between text-[11px] text-slate-400 mt-2">
                <span><span className="inline-block w-2 h-2 rounded-full bg-brand mr-1" />Fixed {Math.round(fixedPct)}%</span>
                <span><span className="inline-block w-2 h-2 rounded-full bg-emerald-400 mr-1" />Variable {Math.round(100 - fixedPct)}%</span>
              </div>
            </div>

            <div className="mt-8 pt-6 border-t border-white/10 space-y-3">
              <Row label="Fixed costs / month" value={money(r.fixedMonthly)} muted />
              <Row label="Total operating cost / month" value={money(r.monthlyOperating)} muted />
            </div>

            <div className="mt-6 flex items-start gap-2 text-xs text-slate-400 leading-relaxed">
              <TrendingUp className="w-4 h-4 text-brand shrink-0 mt-0.5" />
              Any load paying under {perMile(r.totalCPM)}/mile loses you money. Aim for {perMile(r.suggestedRate)}+ to hit your margin.
            </div>

            {benchmark?.sample_size >= 5 && (
              <div className="mt-6 pt-6 border-t border-white/10" data-testid="benchmark-panel">
                <div className="text-xs tracking-[0.2em] uppercase font-bold text-slate-400 mb-3">
                  You vs. {benchmark.sample_size} {benchmark.segmented ? `${eqLabel(benchmark.equipment)} operators` : "owner-operators"}
                </div>
                {(() => {
                  const beatsAvg = r.totalCPM <= benchmark.avg_cpm;
                  const lo = benchmark.p25, hi = benchmark.p75;
                  const span = Math.max(hi - lo, 0.01);
                  const pos = Math.min(100, Math.max(0, ((r.totalCPM - lo) / span) * 100));
                  return (
                    <>
                      <div className="relative h-2 rounded-full bg-white/10 mt-6 mb-2">
                        <div className="absolute inset-y-0 left-1/4 right-1/4 bg-emerald-400/30 rounded-full" />
                        <div className="absolute -top-1 w-4 h-4 rounded-full bg-brand border-2 border-slate-900 -translate-x-1/2" style={{ left: `${pos}%` }} data-testid="benchmark-marker" />
                      </div>
                      <div className="flex justify-between text-[11px] text-slate-500">
                        <span>${lo.toFixed(2)} (lean)</span>
                        <span>${hi.toFixed(2)} (high cost)</span>
                      </div>
                      <p className="text-sm text-slate-300 mt-3">
                        Peer average is <span className="font-mono font-bold text-white">{perMile(benchmark.avg_cpm)}</span>/mi.
                        {" "}
                        {beatsAvg
                          ? <span className="text-emerald-400 font-semibold">Your costs are below average — a real edge.</span>
                          : <span className="text-amber-400 font-semibold">Your costs run above average — trim fixed or fuel costs.</span>}
                      </p>
                    </>
                  );
                })()}
              </div>
            )}
          </motion.div>
          <p className="text-xs text-slate-400 mt-4 text-center">Estimates only — not financial advice.</p>

          <div className="mt-5 border border-slate-200 rounded-md p-5 bg-orange-50/40" data-testid="cpm-strategy-cta">
            <div className="flex items-center gap-2 mb-1.5">
              <Route className="w-4 h-4 text-brand" />
              <h3 className="font-heading text-base font-bold text-slate-900">Turn these numbers into profitable lanes</h3>
            </div>
            <p className="text-sm text-slate-600 leading-relaxed mb-4">
              {STRATEGIST.name.split(" ")[0]} can review your cost-per-mile and help you target lanes that clear your break-even with margin to spare.
            </p>
            <StrategyCallDialog source="strategy_cpm" triggerClassName="h-11 px-6 rounded-full bg-brand hover:bg-brand-hover text-white text-sm font-bold w-full" />
          </div>
        </div>
      </section>
    </div>
  );
}

const Row = ({ label, value, highlight, muted, testid }) => (
  <div className="flex items-center justify-between">
    <span className={`text-sm ${muted ? "text-slate-400" : "text-slate-300"}`}>{label}</span>
    <span data-testid={testid} className={`font-mono tabular font-bold ${highlight ? "text-emerald-400 text-lg" : muted ? "text-slate-300" : "text-white"}`}>{value}</span>
  </div>
);
